import React, { MouseEvent, useEffect, useMemo, useState } from "react";
import { defineMessages, IntlShape, useIntl } from "react-intl";
import { IconHomeHand } from "@tabler/icons-react";
import {
  autoUpdate,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useMergeRefs
} from "@floating-ui/react";
import { classNames, useIsMounted } from "@ct-react/core";
import { HomealpContextType } from "../../../models/search";
import {
  RequiredMainSearchInputProps,
  RequiredSearchInputSelectionProps,
  RequiredSearchInputViewProps,
  SearchInputDefinitionProps
} from "./common";
import "./common.scss";
import "./articletype-input.scss"

const transDefs = defineMessages({
  label: { id: "search-form-articletype-input-label", defaultMessage: "Type" },
  saleValue: { id: "search-form-articletype-sale-value", defaultMessage: "Achat" },
  rentValue: { id: "search-form-articletype-rental-value", defaultMessage: "Location" }
});

export const articleTypeFormattedState = (intl: IntlShape, val: HomealpContextType) => {
  switch (val) {
    case "sale":
      return intl.formatMessage(transDefs.saleValue);
    case "rental":
      return intl.formatMessage(transDefs.rentValue);
  }
}

type InputViewProps = SearchInputDefinitionProps & RequiredSearchInputViewProps<HomealpContextType>;
type InputSelectionProps = RequiredSearchInputSelectionProps<HomealpContextType>;
type MainSearchArticleTypeInputProps = RequiredMainSearchInputProps<HomealpContextType>;

const InputSelection = (
  {
    value,
    onSelection
  }: InputSelectionProps) => {

  const intl = useIntl();

  return(
    <div className="article-type-input-choice">
      <ul>
        {[ "sale", "rental" ].map((r, i: number) => {
          const val = r as HomealpContextType;
          return (<li key={i}
                      className={classNames("selectable-article-type", { selected: val === value })}
                      onClick={e => onSelection(val, e)}>{articleTypeFormattedState(intl, val)}</li>);
        })}
      </ul>
    </div>);

}

const InputMobileView = (
  {
    name,
    open,
    toggle,
    value,
    formattedValue,
    onValueChange
  }: InputViewProps) => {

  const intl = useIntl();

  // dom interactions

  const onSelection = (val: HomealpContextType, _?: MouseEvent) => onValueChange(val);

  // rendering

  const wrapperClasses = classNames("input-wrap", name, { selection: open });

  const editionRendering = useMemo(() => (<>
    <h2 onClick={() => toggle(name, false)}>{intl.formatMessage(transDefs.label)}</h2>
    <InputSelection {...{ value , onSelection }} />
  </>), [ value ]);

  const resumeRendering = useMemo(() => (
    <>
      <div className="input-label ellipsed">{intl.formatMessage(transDefs.label)}</div>
      <div className={classNames("input-val-state", { empty: !value })}>
        <span className="value ellipsed">{formattedValue}</span>
      </div>
    </>
  ), [ value, formattedValue ]);

  return (
    <div tabIndex={0}
         className={wrapperClasses}
         onClick={() => !open && toggle(name, true)}>
      {open ? editionRendering : resumeRendering}
    </div>);

};

const InputDesktopView = (
  {
    name,
    open,
    toggle,
    value,
    formattedValue,
    onValueChange
  }: InputViewProps) => {

  const intl = useIntl();

  // picker floating display

  const { x, y, reference, floating, strategy, context } = useFloating({
    open: open,
    onOpenChange: isOpen => toggle(name, isOpen),
    whileElementsMounted: autoUpdate,
    placement: "bottom-start",
    middleware: [ offset(8) ]
  })

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(context),
    useDismiss(context, { enabled: open, ancestorScroll: true })
  ]);

  // dom interaction

  const onSelection = (val: HomealpContextType, _?: MouseEvent) => {
    onValueChange(val);
    toggle(name, false);
  }

  // rendering

  const inputRef = useMergeRefs([ reference ]);
  const inputWrapperClasses = classNames("input-wrap", name);
  const pickerClasses = classNames("rf-searcher-input-dropdown", name);
  return (
    <>
      <div className={inputWrapperClasses}>
        <div ref={inputRef}
             {...getReferenceProps()}
             tabIndex={0}
             className="input-content inner-space">
          <IconHomeHand />
          <div className="input-val-wrap">
            <div className="input-label ellipsed">{intl.formatMessage(transDefs.label)}</div>
            <div className={classNames("input-val-state", { empty: !value })}>
              <span className="value ellipsed">{formattedValue}</span>
            </div>
          </div>
        </div>

      </div>
      <FloatingPortal>
        {open &&
          <FloatingFocusManager context={context}>
            <div ref={floating}
                 {...getFloatingProps()}
                 className={pickerClasses}
                 style={{ position: strategy, top: y ?? 0, left: x ?? 0 }}>
              <InputSelection {...{value, onSelection }} />
            </div>
          </FloatingFocusManager>
        }
      </FloatingPortal>
    </>);

};

const MainSearchArticleTypeInput = (
  {
    name,
    open,
    toggle,
    mobileView,
    value: initedValue,
    onValueChange
  }: MainSearchArticleTypeInputProps) => {

  const isMounted = useIsMounted();
  const intl = useIntl();

  // component states

  const [ value, setValue ] = useState<HomealpContextType>(initedValue);

  useEffect(() => {
    if (!isMounted) return;
    onValueChange(value);
  }, [ value ]);

  const formattedValue = useMemo(() => articleTypeFormattedState(intl, value), [ value, intl ]);

  // rendering

  if (mobileView)
    return (
      <InputMobileView name={name}
                       open={open}
                       toggle={toggle}
                       value={value}
                       formattedValue={formattedValue}
                       onValueChange={value => setValue(value)} />);
  return (
    <InputDesktopView name={name}
                      open={open}
                      toggle={toggle}
                      value={value}
                      formattedValue={formattedValue}
                      onValueChange={value => setValue(value)} />);

}

export default MainSearchArticleTypeInput;
